import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { v4 as uuid4 } from 'uuid';
import moment from 'moment';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { DomSanitizer, SafeStyle, SafeUrl } from '@angular/platform-browser';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SubsectionOptionEditComponent } from '../../../project-subsection/options/edit/subsection-option-edit.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AudioAnnotation } from '../audio-annotation/audio-annotation.component';
import {
  Annotation,
  AnnotationService,
  onAddingAnnotation,
  onAddingAnnotationDataChanged,
  onAddNewAnnotation,
  onAnnotationClickClosed,
  onAnnotationClickClosedV2,
  onAnnotationClickedIn,
  onAnnotationClickedOut,
  onAnnotationClickOpened,
  onAnnotationClickOpenedV2,
  onAnnotationHoverClosed,
  onAnnotationHoveredIn,
  onAnnotationHoveredOut,
  onAnnotationHoverOpened,
  onAnnotationMarkerSelectedActiveClick,
  onAnnotationMarkerSelectedActiveHover,
  onAnnotationMarkerSelectedInActiveClick,
  onAnnotationMarkerSelectedInActiveHover,
  onAnnotationsLoaded,
  onAnnotationsStateChanged,
  onCancelAddingAnnotation,
  onCloseActiveAnnotation,
  onDeleteAnnotation,
  onDeleteCommentAnnotation,
  onEditingAnnotation,
  onImageZoomChanged,
  onImageViewportChanged,
  onOpenAnnotation,
  onPlayerReady,
  onPluginReady,
  onReplyCommentAnnotation,
  onSaveEditedAnnotation,
  onTimeFormatChange,
  onTimeFormatChangePlugin,
  onVideoPlayerDisposed,
  wsAnnotationChange,
} from '../../services/annotation.service';
import { AuthService } from '../../services/auth.service';
import { EmitterService } from '../../services/emitter.service';
import { HelperService } from '../../services/helper.service';
import {
  WS_SUBSCRIBE_TOPIC_TYPES,
  wsListenerANNOTATION_ADDED,
  wsListenerANNOTATION_CREATED,
  wsListenerANNOTATION_DELETED,
  wsListenerANNOTATION_EDITED,
  wsListenerCOMMENT_CREATED,
  wsListenerCOMMENT_DELETED,
  wsListenerCOMMENT_EDITED,
  MtmStompService,
} from '../../services/mtm-stomp.service';
import { OverlayService } from '../../services/overlayService';
import { FileTransferService, MTMCustomFile, TransferStatus } from '../../services/signed-url';
import { MTMFileDownload } from '../../services/signed-url/mtm-file-download';
import { SignedUrlService } from '../../services/signedurl.service';
import { SubsectionService } from '../../services/subsection.service';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import {
  ActiveSubsectionState,
  AnnotationHighlightSelectedAnnotation,
  AnnotationPrivateCondition,
  MalwareScanningStatus,
  OptionInfo,
  QueryState,
  SimpleFilePage,
  SimpleFilePages,
  TextEditorSelectPrivateCondition,
  TextEditorSelectPrivateConditionProps,
} from '../../interfaces';
import { FileTranscodedEvent } from '../../models';
import { PermissionService } from '../../services/permissions.service';
import { ProjectService } from '../../services/project.service';
import { MTMFileDownloadService, onDownloadStart } from '../mtm-file-download/mtm-file-download.service';
import { ReviewUiLinkSettingsComponent } from '../review-link/review-link-settings/review-link-settings.component';
import {
  SubsectionVersionListComponent,
  SubsectionVersionListResult,
} from '../subsection-version-list/subsection-version-list.component';
import { FailedTranscodingTypes } from './components/pdf-transcode-error/pdf-transcode-error.component';

import {
  TranscodeMonitorResult,
  TranscodeMonitorService,
  TranscodeMonitorType,
} from '../../services/transcode-monitor.service';
import { TopMediaInfoService } from '../top-media-info/top-media-info.service';
import { SafeResourceUrlService } from '../../services/safe-resource-url.service';
import { VideoAnnotationPlugin } from '../video-annotation/plugin/video-annotation.plugin';

import '../../services/videojs-plugins/videojs-download';
import '../../services/videojs-plugins/videojs-resolution-switcher';
import videojs from 'video.js';
import { FileUtilsService } from '../../services/file-utils.service';
import { FileTypeService } from '../../services/file-type.service';
import { GeneralUtilsService } from '../../services/general-utils.service';
import { isTimestamp } from '../../services/is-timestamp.util';
import { StyleHelperService } from '../../services/style-helper.service';
import { ProjectFileService } from '../../services/project-file.service';
import { currentAppToken } from '../../auth/app-token';

@Component({
  selector: 'mtm-media-player',
  templateUrl: './media-player.component.html',
  styleUrl: './media-player.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class MediaPlayerComponent implements OnInit, OnChanges, DoCheck, OnDestroy, AfterViewInit {
  private readonly subsectionService = inject(SubsectionService);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly modalService = inject(NgbModal);
  private readonly overlayService = inject(OverlayService);
  private readonly permissionService = inject(PermissionService);
  private readonly signedUrlService = inject(SignedUrlService);
  private readonly fileUtilsService = inject(FileUtilsService);
  private readonly generalUtilsService = inject(GeneralUtilsService);
  private readonly helperService = inject(HelperService);

  @Output() stateChanged: EventEmitter<any> = new EventEmitter();
  @Output() videoPlayerReady: EventEmitter<any> = new EventEmitter();
  @Output() switchVersionClick = new EventEmitter<Partial<ActiveSubsectionState>>();
  @Input() hideAnnotationToolbar: boolean;
  @Input() enableVideoAnnotation: boolean;
  @Input() enableImageAnnotation: boolean;
  @Input() enableAudioAnnotation: boolean;
  @Input() enableGeneralComments: boolean = true;
  @Input() isSubsectionBarVisible: boolean = false;
  @Input() projectId: any;
  @Input() sectionId: any;
  @Input() subsectionId: any;
  @Input() typeId: any;
  @Input() item: any = null;
  @Input() ngClassState: boolean = false;
  @Input() containerWidth = '320';
  @Input() width: string = '400';
  @Input() height: string = '300';
  @Input() fix: boolean = false;
  @Input() fillContainer: boolean = false;
  @Input() mode: string = null;
  @Input() isSideImg: boolean = false;
  @Input() vrProjection: string;
  @Input() projectParticipants: any[];
  @Input() canAddAnnotationComment: boolean = false;
  @Input() miniPlayer: boolean = false;
  @Input() customSize: boolean = false;
  @Input() autoPlayVideo: boolean = false;
  @Input() showDownloadButtonIfNotPreviewAble: boolean = false;
  @Input() portfolioPreview: boolean = false;
  @Input() reorderPreview: boolean = false;
  @Input() previewContainerHeight: string;
  @Input() subTitleFile: boolean = false;
  @Input() filePreview: boolean = false;
  @Input() enableSubtitle: boolean = false;
  @Input() subtitle: any;
  @Input() isExportFormat: boolean = false;
  @Input() isClickableImage: boolean = false;
  @Input() videoSubtitle: any;
  @Input() videoPreloadMethod: any;
  @Input() audioPreloadMethod: string = 'metadata';
  @Input() customPaddingTop: string | null = null;
  @Input() respondToTaskQueue: boolean = false;
  @Input() directPreview: boolean = false;
  @Input() previewSubtitle: boolean = false;
  @Input() showCloseButton: boolean = false;
  @Input() reviewLink: any;
  @Input() hideTopMediaInfo: boolean = false;
  @Input() parentClass: string = '';
  @Input() subsectionNavigationEnabled: boolean = true;
  @Input() editingEnabled: boolean = true;
  @Input() instanceId: string;
  @Input() isVersionListVisible: boolean = false;
  @Input() shareable: boolean = true;
  @Input() enableResponsive: boolean = false;
  @Input() parentElementContainer: string;
  @Input() filePages: SimpleFilePages | null = null;
  @Input() activePage: SimpleFilePage | null = null;
  @Input() enablePage: boolean = false;
  @Input() commentOnly: boolean = false;
  @Input() enableFallbackDisplay: boolean = true;
  @Input() ignoreOldItem: boolean = false;
  @Input() isKpfPreview: boolean = false;
  @Input() shareLinkPreview: boolean = false;

  privateCondition: AnnotationPrivateCondition = null;

  private _isAssetListVisible: boolean = false;
  metadataCheckTimeout: any;
  get isAssetListVisible() {
    return this._isAssetListVisible;
  }

  @Input()
  set isAssetListVisible(value: boolean) {
    this._isAssetListVisible = value;
    this.isAssetListVisibleChange.emit(value);
  }
  @Output() isAssetListVisibleChange = new EventEmitter<boolean>();

  private _isAssetInfoVisible: boolean = false;
  get isAssetInfoVisible() {
    return this._isAssetInfoVisible;
  }

  @Input()
  set isAssetInfoVisible(value: boolean) {
    this._isAssetInfoVisible = value;
    this.isAssetInfoVisibleChange.emit(value);
  }

  @Output() isAssetInfoVisibleChange = new EventEmitter<boolean>();

  @ViewChild('mtmAudioPlayer') mtmAudioPlayer: ElementRef = null;
  @ViewChild('audioAnnotationCmp') audioAnnotationComponent: AudioAnnotation;
  @ViewChild('audioEl') audioEl: ElementRef;
  @ViewChild('videoPlayer') videoPlayer: ElementRef;
  @ViewChild('youtube') youtube: any;
  @ViewChild('loadingVideoContainer') loadingVideoContainer: ElementRef;
  @ViewChild('videoContainer') videoContainer: ElementRef;
  @ViewChild('videoTools') videoTools: ElementRef;

  player: any;
  annotations: any = [];
  videoOptions: any;
  isPlayerReady: boolean = false;
  isImagePluginReady: boolean = false;
  isVideoPlayerLoaded: boolean = false;
  isVideoPluginReady: boolean = false;

  file: any = null;
  mediaType: string = null;
  audioImage: string = null;
  displayInfo: any = {};
  imgSrc: SafeStyle;
  audioSource: any;
  videoSource: SafeUrl;
  //private isVideoSourceSet: boolean = false;
  private readonly videoSourceCheckInterval: any;
  private readonly videoTypeCheckInterval: any;
  isBusy: boolean = false;
  annotationPlugin: any;
  private componentState: any = {
    drawingMode: false,
  };

  activeAnnotationId: any;
  fileDownloadInfo: MTMFileDownload;
  isAudioPluginReady: boolean;
  userInfo: any;
  ngUnsubscribe = new Subject();
  itemChange$ = new Subject<void>();
  wavesurfer: any;
  videoValidCheckInterval: any;
  isError: boolean = false;
  uuid: any;
  currentFontSubtitle: any;
  currentJsonStyleSubtitle: any;
  isCommentPluginReady: boolean;
  annotCreateListener: any;
  wsListeners: any[] = [];
  wsId: string;
  fileTranscodingStatus: string = 'success';
  isFileNotSupported: boolean = false;
  transcodingTimeoutId: ReturnType<typeof setTimeout>;
  transcodingFailedTimeout: number = 30 * 60 * 1000;
  TranscodingTypes = FailedTranscodingTypes;
  protected readonly MalwareScanningStatus = MalwareScanningStatus;
  subSectionState: ActiveSubsectionState;
  optionsState: QueryState<OptionInfo>;
  annotationsLoaded: boolean = false;
  hasEditPermission: boolean = false;
  isLoading = false;
  fileIdToMonitor: string = '';
  useFallbackDisplay: boolean = false;
  private transcodeSubscription: Record<string, Subscription> = {};
  private readonly replacementIdsKey = 'replacementFileIds';
  private readonly fallbackTimeout = 24 * 60 * 1000;

  //videoGap: number = 10;
  audioGap: number = 52;

  oldItem: boolean = false;

  imagePristine: boolean = false;

  pdfSlideSelectedIndex: number = 0;

  isShareLinkPreview: boolean = false;

  isReleasePreview: boolean = false;
  pageReload: boolean = false;

  get isFullscreen() {
    return this.topMediaInfoService.isFullscreen;
  }

  get isCommentsCollapsed() {
    return this.subsectionService.uiState?.commentsCollapsed;
  }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly safeResourceUrlService: SafeResourceUrlService,
    private readonly authService: AuthService,
    private readonly annotationService: AnnotationService,
    private readonly projectService: ProjectService,
    private readonly projectFileService: ProjectFileService,
    private readonly _http: HttpClient,
    private readonly mtmFileDownloadService: MTMFileDownloadService,
    private readonly transferService: FileTransferService,
    private readonly stompService: MtmStompService,
    public readonly activeModal: NgbActiveModal,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly transcodeMonitorService: TranscodeMonitorService,
    private readonly fileTypeService: FileTypeService,
    private readonly topMediaInfoService: TopMediaInfoService
  ) {
    document.body.className += ' media-preview-page-active';

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.commentsCollapsed === curr.commentsCollapsed)
      )
      .subscribe({
        next: (state) => {
          this.hideAnnotationToolbar = state.commentsCollapsed;
          //this.videoGap = this.hideAnnotationToolbar || !this.isVideoPlayerLoaded ? 10 : 76;
          this.audioGap = this.hideAnnotationToolbar || !this.isAudioPluginReady ? 52 : 124;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isShareLinkPreview === curr.isShareLinkPreview)
      )
      .subscribe({
        next: (state) => {
          this.isShareLinkPreview = state.isShareLinkPreview;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isReleasePreview === curr.isReleasePreview)
      )
      .subscribe({
        next: (state) => {
          this.isReleasePreview = state.isReleasePreview;
        },
      });

    this.topMediaInfoService
      .state$()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isFullscreen === curr.isFullscreen)
      )
      .subscribe({
        next: (state) => {
          this.toggleFullscreen();
        },
      });
  }

  @HostListener('window:resize')
  updateViewportHeight() {
    const vh = document.body.clientHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  ngAfterViewInit() {
    this.wsId = this.stompService.subscribeToService();
    onAddNewAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onAddNewAnnotationHandler(args),
    });

    onDeleteAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onDeleteAnnotation(args),
    });

    onOpenAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onOpenAnnotation(args),
    });

    onCloseActiveAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onCloseActiveAnnotation(args),
    });

    onReplyCommentAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onReplyCommentAnnotation(args),
    });

    // onDeleteCommentAnnotation.pipe(
    // 	takeUntil(this.ngUnsubscribe)
    // ).subscribe({
    // 	next: (args: any) => this.onDeleteCommentAnnotation(args)
    // })

    onAnnotationMarkerSelectedActiveHover.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onAnnotationMarkerSelectedActiveHover(args),
    });

    onAnnotationMarkerSelectedInActiveHover.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onAnnotationMarkerSelectedInActiveHover(args),
    });

    onAnnotationMarkerSelectedActiveClick.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onAnnotationMarkerSelectedActiveClick(args),
    });

    onAnnotationMarkerSelectedInActiveClick.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onAnnotationMarkerSelectedInActiveClick(args),
    });

    onTimeFormatChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onTimeFormatChange(args),
    });

    onPluginReady.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => {
        if (args.type === 'image') {
          this.setImageAnnotationConfig(args);
        } else if (args.type === 'audio') {
          this.setAudioAnnotationConfig(args);
        } else if (args.type === 'video') {
          this.setVideoAnnotationConfig(args);
        } else if (args.type === 'comment') {
          this.setCommentPluginConfig(args);
        }
      },
    });

    onEditingAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onEditingAnnotationHandler(args),
    });

    onSaveEditedAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onSaveEditedAnnotationHandler(args),
    });

    // this.wsRxSubscribe('ANNOTATION_CREATED', wsListenerANNOTATION_CREATED, (args: any) => this.wsOnAnnotationCreate(args));
    // this.wsRxSubscribe('ANNOTATION_EDITED', wsListenerANNOTATION_EDITED, (args: any) => this.wsOnAnnotationEdit(args));
    // this.wsRxSubscribe('ANNOTATION_DELETED', wsListenerANNOTATION_DELETED, (args: any) => this.wsOnAnnotationDelete(args));
    // this.wsRxSubscribe('COMMENT_CREATED', wsListenerCOMMENT_CREATED, (args: any) => this.wsOnCommentCreate(args));
    // this.wsRxSubscribe('COMMENT_EDITED', wsListenerCOMMENT_EDITED, (args: any) => this.wsOnCommentEdit(args));
    // this.wsRxSubscribe('COMMENT_DELETED', wsListenerCOMMENT_DELETED, (args: any) => this.wsOnCommentDelete(args));

    // TODO: Reimplement websockets on new BE
    // this.stompService.subscribeToListener('NEW_ANNOTATION_ADDED', this.wsId, [this.projectId, this.sectionId, this.subsectionId],
    // 	(args) => this.wsNewAnnotationAddedHandler(args), wsListenerANNOTATION_ADDED);
    // this.stompService.subscribeToListener('ANNOTATION_CREATED', this.wsId, [this.projectId, this.sectionId, this.subsectionId],
    // 	(args: any) => this.wsOnAnnotationCreate(args), wsListenerANNOTATION_CREATED);
    // this.stompService.subscribeToListener('ANNOTATION_EDITED', this.wsId, [this.projectId, this.sectionId, this.subsectionId],
    // 	(args: any) => this.wsOnAnnotationEdit(args), wsListenerANNOTATION_EDITED);
    // this.stompService.subscribeToListener('ANNOTATION_DELETED', this.wsId, [this.projectId, this.sectionId, this.subsectionId],
    // 	(args: any) => this.wsOnAnnotationDelete(args), wsListenerANNOTATION_DELETED);
    // this.stompService.subscribeToListener('COMMENT_CREATED', this.wsId, [this.projectId, this.sectionId, this.subsectionId],
    // 	(args: any) => this.wsOnCommentCreate(args), wsListenerCOMMENT_CREATED);
    // this.stompService.subscribeToListener('COMMENT_EDITED', this.wsId, [this.projectId, this.sectionId, this.subsectionId],
    // 	(args: any) => this.wsOnCommentEdit(args), wsListenerCOMMENT_EDITED);
    // this.stompService.subscribeToListener('COMMENT_DELETED', this.wsId, [this.projectId, this.sectionId, this.subsectionId],
    // 	(args: any) => this.wsOnCommentDelete(args), wsListenerCOMMENT_DELETED);

    // this.wsRxSubscribe('CONVERSATION', wsListenerCONVERSATION, (args: any) => this.wsconversation(args));

    // this.stompService.subscribeToTopic(WS_SUBSCRIBE_TOPIC_TYPES['CONVERSATION'], [this.authService.getAuthUser().username]);
    // let listener = wsListenerCONVERSATION.subscribe((data) => this.wsconversation(JSON.parse(data.body)));

    // this.wsListeners.push({"topic": 'CONVERSATION', "listener": listener});

    EmitterService.get('IMAGE_ANNOTATION:RESETTED')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.imagePristine = false;
        },
      });
    EmitterService.get('IMAGE_ANNOTATION:MOVE_TOGGLED')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.imagePristine = false;
        },
      });
    EmitterService.get('IMAGE_ANNOTATION:ZOOMED')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.imagePristine = false;
        },
      });
  }

  /**
   * WEBSOCKET IMPLENTATION FUNCTIONS
   */

  wsRxSubscribe(topic, event, func) {
    this.stompService.subscribeToTopic(WS_SUBSCRIBE_TOPIC_TYPES[topic], [
      this.projectId,
      this.sectionId,
      this.subsectionId,
    ]);
    const listener = event.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => func(JSON.parse(data.body)));

    this.wsListeners.push({ topic: topic, listener: listener });
  }

  wsRxUnsubscribe(topic, listener) {
    this.stompService.unsubscribeTopic(WS_SUBSCRIBE_TOPIC_TYPES[topic], [
      this.projectId,
      this.sectionId,
      this.subsectionId,
    ]);
    listener.unsubscribe();
  }

  wsconversation(eventData) {
    console.log('newmesage', eventData);
  }

  wsOnAnnotationCreate(eventData) {
    let annotation = eventData.mediaAnnotation;

    if (this.getItemId() === annotation.itemId && this.annotationPlugin) {
      annotation = this.annotationService.getAnnotationViewModel(annotation, this.projectParticipants);
      annotation.isWSCall = true;
      annotation.privateConditionKey = this.privateCondition;
      this.annotationPlugin.fire('newAnnotationUpdate', annotation);
      this.annotations = this.annotationPlugin.annotationState.annotations;
      wsAnnotationChange.emit();
    }
  }

  wsOnAnnotationEdit(eventData) {
    let annotation = eventData.mediaAnnotation;
    if (this.getItemId() === annotation.itemId && this.annotationPlugin) {
      annotation = this.annotationService.getAnnotationViewModel(annotation, this.projectParticipants);
      annotation.isWSCall = true;
      if (this.mediaType !== 'video') {
        this.annotationPlugin.fire('updateAnnotation', annotation);
      } else if (this.mediaType === 'video') {
        annotation.commentStr = annotation.comments[0].body;
        annotation.files = annotation.comments[0].files;
        const annotationData = { annotationId: annotation.id, data: annotation, isWSCall: true };
        this.annotationPlugin.fire('saveEditedAnnotation', annotationData);
      }
      this.annotations = this.annotationPlugin.annotationState.annotations;
      wsAnnotationChange.emit();
    }
  }

  wsOnAnnotationDelete(eventData) {
    let annotation = eventData.mediaAnnotation;
    if (this.getItemId() === annotation.itemId && this.annotationPlugin) {
      annotation = this.annotationService.getAnnotationViewModel(annotation, this.projectParticipants);
      annotation.isWSCall = true;
      this.annotationPlugin.fire('destroyAnnotation', { id: annotation.id, annotation: annotation });
      this.annotations = this.annotationPlugin.annotationState.annotations;
      wsAnnotationChange.emit();
    }
  }

  wsOnCommentCreate(eventData) {
    const annotation = eventData.mediaAnnotation;
    const comment = this.annotationService.getCommentViewModel(eventData.commentCreated, this.projectParticipants);
    if (this.getItemId() === annotation.itemId && this.annotationPlugin) {
      const comment_obj = {
        body: comment.body,
        commentCreated: comment,
        annotationId: annotation.id,
        commentId: comment.id,
        isWSCall: true,
      };
      this.annotationPlugin.fire('newComment', comment_obj);
      this.annotations = this.annotationPlugin.annotationState.annotations;
      wsAnnotationChange.emit();
    }
  }

  wsOnCommentEdit(eventData) {
    const annotation = eventData.mediaAnnotation;
    const comment = this.annotationService.getCommentViewModel(eventData.commentEdited, this.projectParticipants);
    if (this.getItemId() === annotation.itemId && this.annotationPlugin) {
      const comment_obj = {
        body: comment.body,
        files: comment.files,
        commentId: comment.id,
        updatetime: comment.meta.updatetime,
        annotationId: annotation.id,
        isWSCall: true,
      };
      this.annotationPlugin.fire('updateComment', comment_obj);
      this.annotations = this.annotationPlugin.annotationState.annotations;
      wsAnnotationChange.emit();
    }
  }

  wsOnCommentDelete(eventData) {
    const annotation = eventData.mediaAnnotation;
    const comment = this.annotationService.getCommentViewModel(eventData.commentDeleted, this.projectParticipants);
    if (this.getItemId() === annotation.itemId && this.annotationPlugin) {
      const event_obj = { annotation: annotation, id: comment.id, isWSCall: true };
      this.annotationPlugin.fire('destroyComment', event_obj);
      this.annotations = this.annotationPlugin.annotationState.annotations;
      wsAnnotationChange.emit();
    }
  }

  ngOnInit() {
    this.topMediaInfoService.updateState({ showListViewModeButton: false });
    this.updateViewportHeight();
    this.userInfo = this.authService.getAuthUser();
    this.createPlaybackRateComponent();
    this.uuid = uuid4();
    // this.prepareAnnotationSubscription();
    // this.prepareCommentSubscription();

    onAnnotationsLoaded.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (result: any) => {
        if (result.instanceId != this.instanceId || result.itemId != this.getItemId()) {
          return;
        }
        this.annotations = result.data;
      },
    });

    this.annotationService.privateConditionChange$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((newValue: AnnotationPrivateCondition) => {
        this.privateCondition = newValue;
        this.getAnnotations()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe({
            next: (data) => {
              if (this.privateCondition === 'ALL') {
                this.annotations = data;
              } else {
                this.annotations = data.filter((a) => a.isPublic === (this.privateCondition === null));
              }
              if (this.mediaType === 'video' && this.annotationPlugin) {
                // this.annotationPlugin.player.canvasFabric.clear();
                this.annotationPlugin.annotationState.annotations = this.annotations;
              }
            },
          });
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged(
          (prev, curr) =>
            prev.activeOption?.optionId === curr.activeOption?.optionId &&
            prev.activeVersion?.versionNumber === curr.activeVersion?.versionNumber
        )
      )
      .subscribe({
        next: (state) => {
          this.subSectionState = state;
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.activeFile === curr.activeFile)
      )
      .subscribe({
        next: (state) => {
          this.subSectionState = state;
        },
      });

    this.subsectionService.options$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (options) => {
        this.optionsState = options;
      },
    });

    if (this.respondToTaskQueue) {
      this.handleTaskQueueEvents();
    }

    this.checkPermission();

    if (/\/kpf-preview$/.test(window.location.pathname)) {
      // Listen for asset info toggle events from the toggle button
      this.topMediaInfoService
        .onToggleAssetInfo()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          this.toggleAssetInfo();
        });

      // Listen for download asset events from the download button
      this.topMediaInfoService
        .onDownloadAsset()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          this.downloadAsset();
        });
    }
  }

  get isLoadingOrBeingTranscoded(): boolean {
    return this.isLoading || this.fileTranscodingStatus === 'in-progress';
  }

  get isNotCleanOrBeingTranscoded(): boolean {
    return this.isLoadingOrBeingTranscoded || this.item?.malwareScanningStatus != MalwareScanningStatus.Clean;
  }

  private checkPermission() {
    if (this.projectService.project?.id) {
      this.hasEditPermission = this.editingEnabled && this.permissionService.canUpdateProject() && !this.reviewLink;
    }
  }

  public createNewVersion() {
    const modalRef = this.modalService.open(SubsectionOptionEditComponent, {
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    modalRef.componentInstance.optionId = this.subSectionState.activeOption.optionId;
    modalRef.componentInstance.isNewVersion = true;
  }

  public showManageVersions() {
    const option = this.subSectionState.activeOption;
    const modalRef = this.modalService.open(SubsectionVersionListComponent, {
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    modalRef.componentInstance.projectId = option.project;
    modalRef.componentInstance.sectionId = option.section;
    modalRef.componentInstance.subsectionId = option.subsection;
    modalRef.componentInstance.itemId = option.optionId;
    modalRef.componentInstance.versions = [...(option.simpleVersions ?? [])];
    modalRef.componentInstance.option = option;
    modalRef.result
      .then((data: SubsectionVersionListResult) => {
        if (!data) {
          return;
        }
        if (data.type === 'edit') {
          this.editVersion(option.optionId, data.version?.versionNumber);
        } else if (data.type === 'success' && data.newCurrentVersion != null) {
          this.switchVersion(data.newCurrentVersion);
        }
      })
      .catch((err) => console.error(err));
  }

  editVersion(optionId: string, versionNumber: number) {
    const modalRef = this.modalService.open(SubsectionOptionEditComponent, {
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    modalRef.componentInstance.optionId = optionId;
    modalRef.componentInstance.versionNumber = versionNumber;
  }

  public switchVersion(versionNumber: number) {
    const option = this.subSectionState?.activeOption;
    const version = option?.simpleVersions?.find((v) => v.versionNumber === versionNumber);
    this.switchVersionClick.emit({
      activeOption: option,
      activeVersion: version,
    });

    if (this.subsectionNavigationEnabled) {
      if (this.reviewLink) {
        this.subsectionService.navigateReviewLink({
          id: this.reviewLink.id,
          topicId: this.subSectionState.activeOption.topicId,
          optionId: this.subSectionState.activeOption.optionId,
          fileId: this.subSectionState.activeFileId,
          versionNumber: versionNumber,
        });
      } else {
        this.subsectionService.navigateSubsection({
          projectId: this.subSectionState.activeOption.project,
          sectionId: this.subSectionState.activeOption.section,
          subsectionId: this.subSectionState.activeOption.subsection,
          topicId: this.subSectionState.activeOption.topicId,
          optionId: this.subSectionState.activeOption.optionId,
          fileId: this.subSectionState.activeFileId,
          versionNumber: versionNumber,
        });
      }
    }
  }

  reviewLinkSettings: any;
  public openReviewLinkSettings() {
    this.reviewLinkSettings = this.modalService.open(ReviewUiLinkSettingsComponent, {
      size: 'lg',
      backdrop: false,
      modalDialogClass: 'responsive-modal',
    });
    const subSection = this.projectService.project.subsectionsId[this.subsectionId];
    this.reviewLinkSettings.componentInstance.setParams({
      section: subSection.projectSectionId,
      subSection: this.subsectionId,
      parentItem: this.subSectionState.activeOption,
      assets: this.optionsState.data,
      version: this.subSectionState.activeVersion.versionNumber,
      init: true,
    });
  }

  private handleTaskQueueEvents() {
    EmitterService.get(TextEditorSelectPrivateCondition)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data: TextEditorSelectPrivateConditionProps) => {
          const { privateCondition, resolve } = data;
          if (this.privateCondition == privateCondition) {
            resolve(true);
            return;
          }

          this.annotationService.updatePrivateCondition(privateCondition);
          resolve(true);
        },
      });
  }

  onCancelAddingAnnotationHandler(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    if (this.annotationPlugin && this.isPlayerReady) {
      this.annotationPlugin.fire('cancelAddingAnnotation');
    }
  }

  onAnnotationMarkerSelectedActiveHover(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }

    // if (this.annotationPlugin && this.isPlayerReady) {
    //   this.annotationPlugin.fire('openAnnotationHover', args.annotation);
    // }
    // if (this.annotationPlugin && this.mediaType === 'audio') {
    // }
  }

  onAnnotationMarkerSelectedInActiveHover(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    // if (this.annotationPlugin && this.isPlayerReady) {
    //   this.annotationPlugin.fire('closeAnnotationHover');
    // }
  }

  onAnnotationMarkerSelectedActiveClick(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    if (this.annotationPlugin && this.isPlayerReady) {
      this.annotationPlugin.fire('annotationClickIn', { annotation: args.annotation });
    }
  }

  onAnnotationMarkerSelectedInActiveClick(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    if (this.annotationPlugin && this.isPlayerReady) {
      this.annotationPlugin.fire('annotationClickOut');
    }
  }

  // onEditingAnnotationHandler(args: any) {
  // 	if (args.itemId != this.getItemId() || args.instanceId != this.instanceId) {
  // 		return;
  // 	}
  // 	if (this.annotationPlugin) {
  // 		this.annotationPlugin.fire('editAnnotation', args);
  // 	}
  // }

  onSaveEditedAnnotationHandler(args: any) {
    if (args.itemId != this.getItemId() || args.instanceId != this.instanceId) {
      return;
    }
    if (this.annotationPlugin) {
      const shape = JSON.stringify(this.player.canvasFabric);
      args.data = {
        ...args.data,
        shape: shape,
        resolution: {
          height: this.player.canvasFabric.getHeight(),
          width: this.player.canvasFabric.getWidth(),
        },
      };
      // let annotIndex = this.annotations.findIndex((el) => el.id === args.annotationId)
      // if (annotIndex) this.annotations[annotIndex] = args.data;

      this.annotationPlugin.fire('saveEditedAnnotation', { data: args.data, annotationId: args.annotationId });
      // this.saveAnnotation(args.data);
    }
  }

  onEditingAnnotationHandler(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    if (!this.annotationPlugin) {
      return;
    }
    this.annotationPlugin.fire('editingAnnotation', {
      annotation: args.annotation,
      instanceId: this.instanceId,
      itemId: this.getItemId(),
    });
  }

  onAddNewAnnotationHandler(args: any) {
    if (!this.player || !this.isPlayerReady || args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    const shape = JSON.stringify(this.player.canvasFabric);
    args.annotation = {
      ...args.annotation,
      ...{
        shape: JSON.parse(shape),
        resolution: {
          height: this.player.canvasFabric.getHeight(),
          width: this.player.canvasFabric.getWidth(),
        },
      },
    };
    if (this.annotationPlugin && this.isPlayerReady) {
      console.log('newAnnotation', args.annotation, args.itemId);
      this.annotationPlugin.fire('newAnnotation', args.annotation, args.itemId);
    }
  }

  wsNewAnnotationAddedHandler(data: any) {
    if (data.itemId != this.getItemId()) {
      return;
    }
    if (this.annotationPlugin && this.item) {
      data = data.filter((comment) => comment.typeId === this.getTypeId() && comment.itemId === this.getItemId());
      const annotations = new Array<Annotation>();
      data.forEach((annotation: any) => {
        const parent = annotation.comments.filter((comment) => {
          return this.generalUtilsService.isObjectEmpty(comment.parentId);
        });
        const replies = annotation.comments.filter((comment) => {
          return !this.generalUtilsService.isObjectEmpty(comment.parentId);
        });
        replies.sort((commentA: any, commentB: any) => {
          return (commentA?.meta?.datetime ?? 0) - (commentB?.meta?.datetime ?? 0);
        });
        annotation.comments = [...parent, ...replies];
        const entity = this.annotationService.getAnnotationViewModel(annotation, this.projectParticipants);
        annotations.push(entity);
      });
      annotations.sort((annotationA: any, annotationB: any) => {
        return (annotationB.comments?.[0]?.meta?.datetime ?? 0) - (annotationA.comments?.[0]?.meta?.datetime ?? 0);
      });
      const newAnnotations = annotations;
      if (newAnnotations.length) {
        let i: number;
        for (i = 0; i < newAnnotations.length; i++) {
          const oldAnnotationValue = this.annotations.find((a) => a.id === newAnnotations[i].id) as any;
          if (!oldAnnotationValue) {
            this.annotationPlugin.fire('newAnnotationUpdate', {
              comments: newAnnotations[i].comments,
              id: newAnnotations[i].id,
              range: newAnnotations[i].range,
              resolution: newAnnotations[i].resolution,
              shape: newAnnotations[i].shape,
            });
          } else {
            for (let j = 0; j < newAnnotations[i].comments.length; j++) {
              const oldAnnotationCommentsValue = oldAnnotationValue.comments.find(
                (oa) => oa.id === newAnnotations[i].comments[j].id
              );
              if (!oldAnnotationCommentsValue) {
                this.annotationPlugin.fire('newCommentUpdate', {
                  annotationId: newAnnotations[i].id,
                  comment: newAnnotations[i].comments[j],
                });
              }
            }
          }
        }
      }
      for (let k = 0; k < this.annotations.length; k++) {
        const annotation: any = newAnnotations.find((a) => a.id === this.annotations[k].id);
        if (!annotation) {
          this.annotationPlugin.fire('destroyAnnotation', { id: this.annotations[k].id });
          if (!this.activeAnnotationId || this.activeAnnotationId === this.annotations[k].id) {
            this.annotationPlugin.fire('closeActiveAnnotation');
            this.activeAnnotationId = void 0;
          }
        } else {
          const { comments } = annotation;
          for (let l = 0; l < comments.length; l++) {
            const oldComment = this.annotations[k].comments.find((c) => c.id === comments[l].id) as any;
            if (oldComment) {
              if (oldComment.body !== comments[l].body) {
                this.annotationPlugin.fire('updateComment', {
                  annotationId: annotation.id,
                  commentId: comments[l].id,
                  body: comments[l].body,
                });
              }
            }
          }
        }
      }
      this.annotations = annotations;
    }
  }

  onDeleteAnnotation(args: any) {
    if (args.itemId != this.getItemId()) {
      return;
    }
    if (args.annotation && this.annotationPlugin) {
      this.annotationPlugin.fire('destroyAnnotation', { id: args.annotation.id });
    }
  }

  onOpenAnnotation(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    const data = {
      messageId: args.annotation.id,
      messageCreatedAt: args.annotation.comments?.[0]?.meta?.datetime_str,
      itemId: this.annotationPlugin.getItem().id,
      instanceId: this.annotationPlugin.instanceId,
    };
    if (this.mediaType === 'video' && args.annotation && this.annotationPlugin && this.player && this.isPlayerReady) {
      this.annotationPlugin.fire('annotationClickIn', data);
      this.activeAnnotationId = args.annotation.id;
    } else if (this.mediaType === 'audio' && this.annotationPlugin && this.isAudioPluginReady) {
      this.annotationPlugin.fire('annotationClickIn', data);
    } else if (
      (this.mediaType === 'image' || this.mediaType === 'convertibleImage' || !!this.activePage) &&
      this.annotationPlugin &&
      this.isImagePluginReady
    ) {
      this.annotationPlugin.fire('annotationClickIn', data);
    } else if (this.annotationPlugin && this.isCommentPluginReady) {
      this.annotationPlugin.fire('annotationClickIn', data);
    }
  }

  onCloseActiveAnnotation(args: any) {
    if (args.itemId != this.getItemId()) {
      return;
    }
    if (args.annotation && this.annotationPlugin && this.player && this.isPlayerReady) {
      this.annotationPlugin.fire('closeActiveAnnotation');
    }
  }

  onReplyCommentAnnotation(args: any) {
    if (args.itemId != this.getItemId() || args.instanceId != this.instanceId) {
      return;
    }
    if (this.instanceId != args.instanceId) {
      return;
    }
    if (
      args.data.annotation &&
      this.annotationPlugin &&
      args.data.commentBody &&
      ((this.mediaType == 'audio' && this.isAudioPluginReady) ||
        (this.mediaType == 'image' && this.isImagePluginReady) ||
        (this.player && this.isPlayerReady) ||
        this.mediaType) &&
      this.getItemId() == this.annotationPlugin.getItem().id
    ) {
      this.annotationPlugin.fire('newComment', {
        annotationId: args.data.annotation.id,
        body: args.data.commentBody,
      });
    }
  }

  onDeleteCommentAnnotation(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    if (args.comment && this.annotationPlugin) {
      if (this.mediaType === 'video' && this.player && this.isPlayerReady) {
        this.annotationPlugin.fire('destroyComment', { id: args.comment.id });
      } else if (
        (this.mediaType === 'audio' && this.isAudioPluginReady) ||
        (this.mediaType === 'image' && this.isImagePluginReady)
      ) {
        this.annotationPlugin.fire('destroyComment', { id: args.comment.id, annotation: args.annotation });
      }
    }
  }

  onTimeFormatChange(args: any) {
    if (args.itemId != this.getItemId()) {
      return;
    }
    if (args.format && this.annotationPlugin && this.player && this.isPlayerReady) {
      this.annotationPlugin.fire(args.act, { format: args.format });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      let isCurrentAssetChanged = false;
      if (changes.item) {
        this.imagePristine = true;
      }
      if (changes.item?.currentValue) {
        this.oldItem = false;
        this.itemChange$.next();
        this.updatePreviewSize();
        isCurrentAssetChanged = true;
      }

      if (changes.activePage?.currentValue) {
        isCurrentAssetChanged = true;
      }

      if (isCurrentAssetChanged) {
        this.isAssetInfoVisible = false;
      }

      if (
        changes.activePage?.currentValue &&
        (!changes.activePage.isFirstChange() || !changes.activePage.previousValue)
      ) {
        this.refreshActivePage();
      }
      // if (changes.videoSubtitle) {
      // 	this.setVideoSubtitles();
      // }
      if (changes.selectedSubtitle?.currentValue && !changes.selectedSubtitle.firstChange) {
        if (this.player) {
          const subtitle = this.player.textTracks();
          for (var i = 0; i < subtitle.tracks_.length; i++) {
            var track = subtitle.tracks_[i];
            if (track.kind === 'captions' && track.language === changes.selectedSubtitle.currentValue) {
              track.mode = 'showing';
            } else {
              track.mode = 'hidden';
            }
          }
        }
      }
      if (changes.previewSubtitle?.currentValue) {
        if (this.mediaType === 'video' && this.player) {
          this.player.pause();
          this.player.currentTime(0.1);
        }
      }
    }
  }

  ngDoCheck(): void {
    let isVacDisablePlay = false;
    if (!this.player || !this.isPlayerReady) {
      isVacDisablePlay = false;
    } else {
      isVacDisablePlay =
        this.player.el().classList.contains('vac-active') && this.player.el().classList.contains('vac-disable-play');
    }
    if (isVacDisablePlay !== this.componentState.drawingMode) {
      this.componentState = { ...this.componentState, ...{ drawingMode: isVacDisablePlay } };
      this.onStateChanged(this.componentState);
    }
    if (this.player && !this.isPlayerReady) {
      const isVacActive = this.player.el().classList.contains('vac-active');
      if (!Number.isNaN(this.player.duration()) && isVacActive) {
        onPlayerReady.emit({
          act: 'onPlayerReady',
          player: this.player,
          annotationPlugin: this.annotationPlugin,
          projectId: this.projectId,
          sectionId: this.sectionId,
          subSectionId: this.subsectionId,
          typeId: this.getTypeId(),
          itemId: this.getItemId(),
          metadata: this.item?.metadata,
          instanceId: this.instanceId,
        });
        this.isPlayerReady = true;
      }
    }
  }

  ngOnDestroy() {
    if (this.fileIdToMonitor) {
      this.transcodeMonitorService.stopMonitoringFile(this.fileIdToMonitor);
    }
    this.disposePlayerIfExist();
    this.disposeAllPlugins();
    this.cancelMetadataCheck();
    this.item = null;
    this.annotationPlugin = null;
    if (this.videoTypeCheckInterval) clearTimeout(this.videoTypeCheckInterval);
    if (this.videoSourceCheckInterval) clearTimeout(this.videoSourceCheckInterval);
    setTimeout(() => {
      const mediaElements = this.elementRef.nativeElement.querySelectorAll('[src]');
      mediaElements.forEach((element) => {
        if (element.nodeName === 'VIDEO') {
          element.pause();
          element.removeAttribute('src');
          element.load();
        } else {
          element.removeAttribute('src');
        }
      });
    }, 350);
    // if (!this.isKpfPreview) {
    // 	document.body.className = document.body.className.replace('media-preview-fullscreen', '');
    // }
    document.body.className = document.body.className.replace('media-preview-page-active', '');

    // Remove the --vh style from html tag
    document.documentElement.style.removeProperty('--vh');

    // this.wsListeners.forEach(listener => {
    // 	this.wsRxUnsubscribe(listener.topic, listener.listener);
    // });
    this.stompService.unsubscribeToService(this.wsId);

    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this.itemChange$.next();
    this.itemChange$.complete();

    if (this.transcodingTimeoutId) {
      clearTimeout(this.transcodingTimeoutId);
    }
    EmitterService.get('MEDIA_PLAYER:FULLSCREEN').emit(false);
  }

  detachSubtitleStyle() {
    this.currentFontSubtitle = this.currentJsonStyleSubtitle = void 0;
    StyleHelperService.addOrRemoveStyle('remove', this.getItemId());
  }

  attachSubtitleStyle(jsonStyle, font) {
    this.currentJsonStyleSubtitle = jsonStyle;
    if (this.currentFontSubtitle?.name !== font.name) {
      this._http.get<Blob>(font.font_url, { observe: 'response', responseType: 'blob' as 'json' }).subscribe(
        (res: HttpResponse<Blob>) => {
          const binaryData = [];
          binaryData.push(res.body);
          window.URL.createObjectURL(new Blob(binaryData, { type: 'font/ttf' }));
          this.currentFontSubtitle = {
            name: font.name,
            font_url: window.URL.createObjectURL(new Blob(binaryData, { type: 'font/ttf' })),
          };
          const elementStyleString = this.generateVideoJsTrackCustomStyle(jsonStyle, this.currentFontSubtitle);
          const validatedJsonStyle = StyleHelperService.validateCssString(elementStyleString);
          const style = StyleHelperService.wrapStyle(validatedJsonStyle);
          StyleHelperService.addOrRemoveStyle('load', this.getItemId(), style);
        },
        (err) => {
          console.log(err);
        }
      );
    } else {
      const elementStyleString = this.generateVideoJsTrackCustomStyle(jsonStyle, this.currentFontSubtitle);
      const validatedJsonStyle = StyleHelperService.validateCssString(elementStyleString);
      const style = StyleHelperService.wrapStyle(validatedJsonStyle);
      StyleHelperService.addOrRemoveStyle('load', this.getItemId(), style);
    }
  }

  refreshActivePage() {
    if (this.item?.malwareScanningStatus !== MalwareScanningStatus.Clean) {
      return;
    }

    this.updateContainerSize(this.item);
    this.cancelMetadataCheck();
    this.isImagePluginReady = false;
    this.disposePlayerIfExist();
    this.disposeAllPlugins();
    this.isError = false;
    this.annotationsLoaded = false;
    this.isImagePluginReady = false;
    if (this.videoTypeCheckInterval) clearTimeout(this.videoTypeCheckInterval);
    if (this.videoSourceCheckInterval) clearTimeout(this.videoSourceCheckInterval);
    this.pageReload = true;
    setTimeout(() => {
      this.annotations = [];
      this.pageReload = false;
      this.init(true);
    }, 0);
  }

  private generateVideoJsTrackCustomStyle(jsonStyle, font) {
    const videoHeight = this.player.videoHeight(),
      videoWidth = this.player.videoWidth();
    const containerHeight = this.player.isFullscreen() ? window.outerHeight : 360.0;
    const containerWidth = videoWidth / (videoHeight / containerHeight);
    let elementStyleString = '';
    if (font) {
      elementStyleString += '@font-face {';
      elementStyleString += `font-family: ${font.name};`;
      elementStyleString += `src: url(${font.font_url});`;
      elementStyleString += `}`;
    }
    elementStyleString += '.vjs-text-track-display {';
    elementStyleString += 'width:' + containerWidth + 'px !important;';
    elementStyleString += 'margin: 0 auto 0 auto !important';
    elementStyleString += '}';
    elementStyleString += '.vjs-text-track-display .vjs-text-track-cue { ';
    elementStyleString += 'height: auto !important;';
    elementStyleString += 'width: auto !important;';
    elementStyleString += 'text-align:' + jsonStyle.hAlignment + ' !important;';
    elementStyleString += 'top:' + (jsonStyle.vAlignment === 'top' ? '0' : 'auto') + ' !important;';
    elementStyleString += 'bottom:' + (jsonStyle.vAlignment === 'bottom' ? '0' : 'auto') + ' !important;';
    elementStyleString += '}';
    elementStyleString += '.vjs-text-track-display .vjs-text-track-cue > div { ';
    elementStyleString +=
      'background-color:' +
      (jsonStyle.text_background.enabled ? jsonStyle.text_background.value : 'inherit') +
      ' !important;';
    elementStyleString += 'color:' + jsonStyle.font_color + ' !important;';
    elementStyleString += 'font-size:' + jsonStyle.font_size + 'px !important;';
    elementStyleString +=
      'text-shadow:' +
      (jsonStyle.text_shadow.enabled ? '2px 2px ' + jsonStyle.text_shadow.value : 'none') +
      ' !important;';
    elementStyleString += 'font-family:' + jsonStyle.font_family + ' !important;';
    elementStyleString += '}';
    return elementStyleString;
  }

  onStateChanged(state: any) {
    this.stateChanged.emit(state);
  }

  updatePreviewSize() {
    if (this.item?.malwareScanningStatus !== MalwareScanningStatus.Clean) {
      return;
    }

    this.updateContainerSize(this.item);
    this.cancelMetadataCheck();
    this.isVideoPluginReady = false;
    this.isImagePluginReady = false;
    this.isAudioPluginReady = false;
    this.disposePlayerIfExist();
    this.disposeAllPlugins();
    this.mediaType = void 0;
    this.isError = false;
    this.isVideoPluginReady = false;
    this.isImagePluginReady = false;
    this.isAudioPluginReady = false;
    if (this.videoTypeCheckInterval) clearTimeout(this.videoTypeCheckInterval);
    if (this.videoSourceCheckInterval) clearTimeout(this.videoSourceCheckInterval);
    setTimeout(() => {
      this.annotations = [];
      this.init();
    }, 0);
  }

  updateContainerSize(item: any) {
    // Class list updated here, css updated in slider component
    const type = this.getMediaType(item);
    const previewContainer = document.getElementById('previewContainer');
    const assetContainer = document.getElementById('assetContainer');

    if (type === 'video' && previewContainer && assetContainer) {
      previewContainer.classList.add('video-selected');
      previewContainer.classList.remove('audio-selected', 'image-selected', 'pdf-selected');
      assetContainer.classList.add('video-selected');
      assetContainer.classList.remove('audio-selected', 'image-selected', 'pdf-selected');
    } else if (type === 'audio' && previewContainer && assetContainer) {
      previewContainer.classList.add('audio-selected');
      previewContainer.classList.remove('video-selected', 'image-selected', 'pdf-selected');
      assetContainer.classList.add('audio-selected');
      assetContainer.classList.remove('video-selected', 'image-selected', 'pdf-selected');
    } else if ((type === 'image' || type == 'convertibleImage') && previewContainer && assetContainer) {
      previewContainer.classList.add('image-selected');
      previewContainer.classList.remove('video-selected', 'audio-selected', 'pdf-selected');
      assetContainer.classList.add('image-selected');
      assetContainer.classList.remove('video-selected', 'audio-selected', 'pdf-selected');
    } else if (
      (type === 'pdf' || type === 'officeDoc' || type == 'powerpoint' || type == 'keynote') &&
      previewContainer &&
      assetContainer
    ) {
      previewContainer.classList.add('pdf-selected');
      previewContainer.classList.remove('video-selected', 'audio-selected', 'image-selected');
      assetContainer.classList.add('pdf-selected');
      assetContainer.classList.remove('video-selected', 'audio-selected', 'image-selected');
    }
  }

  getMediaType(item) {
    let type;
    if (this.fileTypeService.isImageFile(item)) {
      type = 'image';
    } else if (this.fileTypeService.isAudioFile(item)) {
      type = 'audio';
    } else if (this.fileTypeService.isVideoFile(item)) {
      type = 'video';
    } else if (this.fileTypeService.isSubtitleFile(item)) {
      type = 'subtitle';
    } else if (item.contentType == 'application/pdf') {
      type = 'pdf';
    } else if (this.fileTypeService.isOfficeDocWithoutPpt(this.item)) {
      type = 'officeDoc';
    } else if (this.fileTypeService.isConvertibleImageFormat(this.item)) {
      type = 'convertibleImage';
    } else if (this.fileTypeService.isPowerPointFile(this.item)) {
      type = 'powerpoint';
    } else if (this.fileTypeService.isKeynoteFile(this.item)) {
      type = 'keynote';
    }

    return type;
  }

  getAnnotations() {
    const typeId = this.getTypeId();
    const itemId = this.getItemId();

    if (!typeId || !itemId) {
      return of([]);
    }

    const subject = new Subject<any>();

    /*
		this.annotationService
				.getAnnotations(this.projectId, this.sectionId, this.subsectionId, this.getTypeId(), this.getItemId(), this.projectParticipants)
				.pipe(
					takeUntil(this.ngUnsubscribe)
				).subscribe(
					(data) => {
						subject.next(data);
					},
					(err) => {
						subject.error(err);
					}
				);
		 */

    const params = {
      projectId: this.projectId,
      sectionId: this.sectionId,
      subsectionId: this.subsectionId,
      typeId,
      itemId,
      participants: this.projectParticipants,
      privateConditionRequest: null,
    };
    forkJoin([
      this.annotationService.getMediaAnnotations({
        ...params,
        privateConditionRequest: 'DOMAIN',
      }),
      this.annotationService.getMediaAnnotations({
        ...params,
      }),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ([privateAnnotations, publicAnnotations]) => {
          const data = [
            ...privateAnnotations.map((item: Annotation) => ({ ...item, isPublic: false })),
            ...publicAnnotations.map((item: Annotation) => ({ ...item, isPublic: true })),
          ];
          subject.next(data);
        },
      });

    return subject.asObservable();
  }

  disposePlayerIfExist() {
    if (this.player && this.isPlayerReady && this.mediaType === 'video') {
      this.player.dispose();
      this.player = void 0;
      this.isVideoPlayerLoaded = false;
      this.isPlayerReady = false;
      this.annotationPlugin = void 0;
      onVideoPlayerDisposed.emit({
        act: 'videoPlayerDisposed',
        player: this.player,
        annotationPlugin: this.annotationPlugin,
        projectId: this.projectId,
        sectionId: this.sectionId,
        subSectionId: this.subsectionId,
        typeId: this.getTypeId(),
        itemId: this.item?.id,
      });
      $(document).off('click');
    }
  }

  disposeImagePluginIfExist() {
    if (this.annotationPlugin?.eventDispatcher.pluginReady && this.mediaType === 'image') {
      this.annotationPlugin.fire('dispose');
      this.annotationPlugin = void 0;
    }
  }

  disposeAudioPluginIfExist() {
    if (this.annotationPlugin?.eventDispatcher.pluginReady && this.mediaType === 'audio') {
      this.annotationPlugin.fire('dispose');
      this.annotationPlugin = void 0;
    }
  }

  disposeAllPlugins() {
    if (this.annotationPlugin?.eventDispatcher?.pluginReady) {
      this.annotationPlugin.fire('dispose');
      this.annotationPlugin = void 0;
    }
  }

  createPlaybackRateComponent() {
    const playbackRateMenu = videojs.getComponent('PlaybackRateMenuButton');
    class CustomPlaybackRateMenu extends playbackRateMenu {
      constructor(player: any, options?: any) {
        super(player, options);

        // Trigger menu display on menu click
        // Using native addEventListener instead of jQuery
        this.contentEl().addEventListener('click', () => {
          this.el().classList.toggle('vjs-menu-open');
        });
      }
      handleClick() {
        this.el().classList.toggle('vjs-menu-open');
      }
    }
    videojs.registerComponent('customPlaybackRateMenu', CustomPlaybackRateMenu);
  }

  private sendDownloadGAEvent(params: { url: string; quality?: string }) {
    if (!this.file) {
      return;
    }

    try {
      let fileName = this.file?.displayName || this.file?.name;
      if (fileName) {
        fileName = this.fileUtilsService.getFileDisplayName(fileName);
      }

      const downloadEvent: any = {
        fileName,
        fileUrl: params.url,
        fileType: this.file?.contentType,
        quality: params.quality,
        fileId: this.file?.id,
        projectId: this.file?.projectId,
        downloadTime: new Date().toISOString(),
        user: this.authService.getAuthUserName(),
      };
      if (this.file?.sectionId) {
        downloadEvent.sectionId = this.file.sectionId;
      }

      if (this.file?.subSectionId) {
        downloadEvent.subSectionId = this.file.subSectionId;
      }

      if (this.file?.sourceId) {
        downloadEvent.sourceId = this.file.sourceId;
      }

      if (this.file?.versionNumber) {
        downloadEvent.versionNumber = this.file.versionNumber;
      }

      this.googleAnalyticsService.sendEvent('file_download', downloadEvent);
    } catch (error) {
      console.error('Error sending download event to GA', error);
    }
  }

  private init(forceInit?: boolean): void {
    this.isFileNotSupported = false;
    if (this.item?.status === 'uploading') {
      EmitterService.get('file-upload-done-' + this.getItemId())
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          this.updatePreviewSize();
        });
      return;
    }
    this.displayInfo.extension = '';
    const pdfExtensionsToSkip = ['ai'];
    if (this.item && (this.item !== this.file || forceInit)) {
      let ext = '';
      if (this.item?.name) {
        ext = (this.item?.name || '').split('.').pop();
        if (ext != '') {
          this.displayInfo.extension = '.' + ext;
        }
      }
      if (typeof this.item === 'string' || this.item instanceof String) {
        this.file = this.item;
        this.mediaType = 'url';
        if (this.youtube) {
          this.youtube.link = this.item;
          this.youtube.ngOnInit();
        }
      } else if (this.fileTypeService.isVideoFile(this.item)) {
        this.file = this.item;
        //this.isVideoSourceSet = false;
        this.videoSource = this.safeResourceUrlService.toSafeUrl(this.file.signedURL);
        this.initializeVideoAnnotations();
        // this.initializeVideoJs();
        //this.checkValidOriginal();
        // this.videoValidCheckInterval = setInterval(this.checkValidOriginal.bind(this), 5000);
      } else if (this.fileTypeService.isAudioFile(this.item)) {
        this.file = this.item;
        this.audioImage = this.helperService.getAudioImage();
        this.audioSource = this.safeResourceUrlService.toSafeUrl(this.file.signedURL);
        this.prepareWaveSurfer(this.file);
        this.initializeAudioAnnotations();
      } else if (
        this.fileTypeService.isImageFile(this.item) &&
        this.fileTypeService.isSupportedImageFormat(this.item?.contentType)
      ) {
        this.file = this.item;
        this.imgSrc = this.safeResourceUrlService.toSafeStyle('url("' + this.file.signedURL + '")');
        this.initializeImageAnnotations(false);
      } else if (this.item?.contentType == 'application/pdf' && !pdfExtensionsToSkip.includes(ext)) {
        // this.mediaType = 'pdf';
        this.file = this.item;
        this.file.previewSRC = this.file.signedURL;
        this.initializeComments('pdf');
        if (this.enablePage) {
          this.checkFileTranscoding(this.mediaType || 'pdf');
        } else {
          this.mediaType = 'pdf';
        }
      } else if (this.fileTypeService.isOfficeDocWithoutPpt(this.item)) {
        this.mediaType = 'officeDoc';
        this.file = this.item;
        this.file.previewSRC = this.file.signedURL + '?thumbnail=_PDF';
        this.checkFileTranscoding(this.mediaType);
        if (this.fileTranscodingStatus == 'success') {
          this.mediaType = 'pdf';
          this.initializeComments('pdf');
        }
      } else if (this.fileTypeService.isPowerPointFile(this.item)) {
        this.mediaType = 'powerpoint';
        this.file = this.item;
        this.file.previewSRC = this.file.signedURL + '?thumbnail=_PDF';
        this.checkFileTranscoding(this.mediaType);
        if (this.fileTranscodingStatus == 'success') {
          // this.mediaType = 'pdf';
          this.initializeComments('pdf');
        }
      } else if (this.fileTypeService.isSubtitleFile(this.item)) {
        // this.mediaType = 'subtitle';
        this.file = this.item;
        this.initializeComments('subtitle');
      } else if (this.fileTypeService.isConvertibleImageFormat(this.item)) {
        // this.mediaType = 'convertibleImage';
        this.file = this.item;
        this.imgSrc = this.safeResourceUrlService.toSafeStyle('url("' + this.file.signedURL + '?thumbnail=_IMG")');
        this.file.previewSRC = this.file.signedURL + '?thumbnail=_IMG';
        this.checkFileTranscoding('convertibleImage');
        this.initializeImageAnnotations(true);
      } else if (this.item.name.endsWith('.key')) {
        this.file = this.item;
        this.checkFileTranscoding('keynote');

        if (this.fileTranscodingStatus == 'success') {
          this.file.previewSRC = this.file.signedURL + '?thumbnail=_PDF';
          this.initializeComments('pdf');
        }
        this.mediaType = 'keynote';
      } else {
        this.isFileNotSupported = true;
        this.file = this.item;
        this.file.publicLink = this.file.signedURL;
        this.initializeComments('download-link');
        // this.mediaType = 'download-link';
      }
    }
  }

  private switchFileToMonitor(fileId: string) {
    if (this.fileIdToMonitor == fileId) {
      return;
    }

    if (this.fileIdToMonitor) {
      this.transcodeMonitorService.stopMonitoringFile(this.fileIdToMonitor);
    }

    this.fileIdToMonitor = fileId;
  }

  private checkFileTranscoding(mediaType: string) {
    let codec = '';

    if (mediaType === 'convertibleImage') {
      const pngMetadata = this.file?.transcodeMetadata?.find((m) => m.codec === 'png');
      const jpgMetadata = this.file?.transcodeMetadata?.find((m) => m.codec === 'jpg');

      codec = pngMetadata ? 'png' : jpgMetadata ? 'jpg' : '';
      if (!codec) {
        codec = 'png';
      }
    } else if (mediaType === 'officeDoc') {
      codec = 'pdf';
    } else if (mediaType === 'powerpoint' || mediaType === 'keynote') {
      codec = this.enablePage ? 'images' : 'pdf';
    } else if (mediaType === 'pdf') {
      codec = 'images';
    }

    this.checkCurrentTranscodingStatus(codec);

    if (!codec) {
      return;
    }

    if (this.fileTranscodingStatus == 'failed' && this.file.replacementId) {
      this.checkReplacementFile(this.file.replacementId, this.file.id, codec);
      return;
    }

    if (this.fileTranscodingStatus == 'in-progress' && !(this.file.id in this.transcodeSubscription)) {
      this.switchFileToMonitor(this.file.id);
      const obs$ = this.transcodeMonitorService.monitorFile({
        projectId: this.projectId ?? this.projectService.project?.id,
        fileId: this.file.id,
        codec: codec,
        type: codec == 'images' ? TranscodeMonitorType.Pages : TranscodeMonitorType.SingleFile,
      });
      this.transcodeSubscription[this.file.id] = obs$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
        next: (data: TranscodeMonitorResult) => {
          if (!data?.file) {
            return;
          }

          const isSameFile = data.fileId == this.file.id;

          if (isSameFile) {
            this.fileUtilsService.copyFilePropsAfterScanning(data.file, this.file);
          }

          if (data.isSuccess) {
            EmitterService.get(FileTranscodedEvent).emit(data);
          }

          if ((codec == 'images' || codec == 'pdf') && data.isSuccess) {
            if (data.fileId == this.file.id) {
              this.file.previewSRC = this.file.signedURL + '?thumbnail=_PDF';
              this.mediaType = 'pdf';
              this.initializeComments('pdf');
            }
          }

          if (isSameFile) {
            this.fileTranscodingStatus = data.isSuccess ? 'success' : 'failed';
            this.isLoading = false;
          }
        },
      });
    }
  }

  private checkCurrentTranscodingStatus(codec: string) {
    this.useFallbackDisplay = false;
    const elapsedTime = new Date().getTime() - this.file.timeCreated;
    let isTimeout = false;
    const metadata = this.file?.transcodeMetadata?.find((m) => m.codec == codec);
    if (metadata) {
      switch (metadata.resolution) {
        case 'true':
          if (this.enablePage && codec == 'images') {
            if (this.filePages) {
              const ready = this.filePages.pages?.every((c) => c?.status == 'READY');
              if (ready) {
                this.isLoading = false;
                this.fileTranscodingStatus = 'success';
              } else {
                this.isLoading = true;
                this.fileTranscodingStatus = 'in-progress';
              }
            } else {
              this.isLoading = true;
              this.fileTranscodingStatus = 'in-progress';
            }
          } else {
            this.fileTranscodingStatus = 'success';
          }
          break;
        case 'false':
          this.fileTranscodingStatus = 'failed';
          break;
        default:
          if (elapsedTime > this.transcodingFailedTimeout) {
            this.fileTranscodingStatus = 'failed';
            isTimeout = true;
          } else {
            this.fileTranscodingStatus = 'in-progress';
          }
          break;
      }
    } else {
      if (elapsedTime > this.transcodingFailedTimeout) {
        isTimeout = true;
        this.fileTranscodingStatus = 'failed';
      } else {
        this.fileTranscodingStatus = 'in-progress';
      }
    }

    // use fallback if pdf to images transcoding failed
    if (this.fileTranscodingStatus == 'failed' && codec == 'images' && this.enableFallbackDisplay) {
      if (!isTimeout) {
        this.checkFallbackDisplay();
      } else {
        if (elapsedTime > this.fallbackTimeout) {
          this.checkFallbackDisplay();
        }
      }
    }
  }

  private checkFallbackDisplay() {
    if (this.file?.contentType == 'application/pdf') {
      this.fileTranscodingStatus = 'success';
      this.useFallbackDisplay = true;
    } else {
      const pdfMetadata = this.file?.transcodeMetadata?.find((m) => m.codec == 'pdf');
      if (pdfMetadata?.resolution == 'true') {
        this.fileTranscodingStatus = 'success';
        this.useFallbackDisplay = true;
      }
    }
  }

  onVideoThumbnailLoad() {
    if (this.videoContainer) $(this.videoContainer.nativeElement).removeClass('hidden-container');
    if (!this.portfolioPreview && this.videoTools) $(this.videoTools.nativeElement).removeClass('hidden-container');
    if (this.loadingVideoContainer) $(this.loadingVideoContainer.nativeElement).addClass('hidden-container');
  }

  onVideoThumbnailLoadError() {
    if (this.file?.name) {
      if (this.checkMp4(this.file.name)) {
        if (this.videoContainer) $(this.videoContainer.nativeElement).removeClass('hidden-container');
        if (!this.portfolioPreview && this.videoTools) $(this.videoTools.nativeElement).removeClass('hidden-container');
        if (this.loadingVideoContainer) $(this.loadingVideoContainer.nativeElement).addClass('hidden-container');
      }
    } else {
      if (this.videoContainer) $(this.videoContainer.nativeElement).addClass('hidden-container');
      if (!this.portfolioPreview && this.videoTools) $(this.videoTools.nativeElement).addClass('hidden-container');
      if (this.loadingVideoContainer) $(this.loadingVideoContainer.nativeElement).removeClass('hidden-container');
    }
  }

  private initializeVideoAnnotations() {
    if (this.enableAudioAnnotation) {
      if (this.isBusy) return;
      this.isBusy = true;
      this.getAnnotations()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((data) => {
          if (this.privateCondition === 'ALL') {
            this.annotations = data;
          } else {
            this.annotations = data.filter((a) => a.isPublic === (this.privateCondition === null));
          }
          this.mediaType = 'video';
          this.isBusy = false;
          this.userInfo = this.authService.getAuthUser();
        });
    } else {
      this.mediaType = 'video';
    }
  }

  private initializeAudioAnnotations() {
    if (this.enableAudioAnnotation) {
      if (this.isBusy) return;
      this.isBusy = true;
      this.getAnnotations()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((data) => {
          console.log('initializing audio annotations in media player', this.instanceId, this.item?.name, data.length);
          if (this.privateCondition === 'ALL') {
            this.annotations = data;
          } else {
            this.annotations = data.filter((a) => a.isPublic === (this.privateCondition === null));
          }
          this.mediaType = 'audio';
          this.isBusy = false;
          this.userInfo = this.authService.getAuthUser();
        });
    } else {
      this.mediaType = 'audio';
    }
  }

  private initializeImageAnnotations(isConvertibleImage: boolean = false) {
    if (this.enableImageAnnotation) {
      if (this.isBusy) return;
      this.isBusy = true;
      this.getAnnotations()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (data) => {
            if (this.privateCondition === 'ALL') {
              this.annotations = data;
            } else {
              this.annotations = data.filter((a) => a.isPublic === (this.privateCondition === null));
            }
            this.isBusy = false;
            if (isConvertibleImage) {
              this.mediaType = 'convertibleImage';
            } else {
              this.mediaType = 'image';
            }
            this.userInfo = this.authService.getAuthUser();
          },
          (err) => {
            this.isBusy = false;
          }
        );
    } else {
      this.mediaType = 'image';
    }
  }

  private initializeComments(mediaType) {
    if (this.enableGeneralComments) {
      if (this.isBusy) return;
      this.isBusy = true;
      this.getAnnotations()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (data) => {
            if (data) {
              if (this.privateCondition === 'ALL') {
                this.annotations = data;
              } else {
                this.annotations = data.filter((a) => a.isPublic === (this.privateCondition === null));
              }
            }
            this.isBusy = false;
            this.userInfo = this.authService.getAuthUser();
            this.mediaType = mediaType;
            this.annotationsLoaded = true;
          },
          (err) => {
            this.isBusy = false;
          }
        );
    }
  }

  cancelMetadataCheck() {
    if (this.metadataCheckTimeout) {
      clearTimeout(this.metadataCheckTimeout);
      this.metadataCheckTimeout = null;
    }
  }

  setVideoAnnotationConfig(args) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    this.isVideoPluginReady = true;
    this.annotationPlugin = args.annotationPlugin;
    this.initializeAnnotationEvents();
  }

  setAudioAnnotationConfig(args) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    this.isAudioPluginReady = true;
    this.annotationPlugin = args.annotationPlugin;
    this.initializeAudioAnnotationsEvents();
    this.initializeAnnotationEvents();
  }

  setImageAnnotationConfig(args) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    this.isImagePluginReady = true;
    this.annotationPlugin = args.annotationPlugin;
    this.initializeAnnotationEvents();
  }

  setCommentPluginConfig(args) {
    if (args.instanceId != this.instanceId || args.itemId != this.getItemId()) {
      return;
    }
    this.isCommentPluginReady = true;
    this.annotationPlugin = args.annotationPlugin;
    this.initializeAnnotationEvents();
  }

  initializeAudioAnnotationsEvents() {
    if (this.mediaType === 'audio' && this.isAudioPluginReady && this.annotationPlugin) {
      this.annotationPlugin.registerListener('annotationHoveredIn', (event: any) => {
        if (event.detail.instanceId != this.instanceId || event.detail.itemId != this.getItemId()) {
          return;
        }
        onAnnotationHoveredIn.emit({
          act: 'onAnnotationHoveredIn',
          projectId: this.projectId,
          sectionId: this.sectionId,
          subSectionId: this.subsectionId,
          typeId: this.getTypeId(),
          itemId: this.getItemId(),
          instanceId: this.instanceId,
          data: event.detail,
        });
      });

      this.annotationPlugin.registerListener('annotationHoveredOut', (event: any) => {
        if (event.detail.instanceId != this.instanceId || event.detail.itemId != this.getItemId()) {
          return;
        }
        onAnnotationHoveredOut.emit({
          act: 'onAnnotationHoveredOut',
          projectId: this.projectId,
          sectionId: this.sectionId,
          subSectionId: this.subsectionId,
          typeId: this.getTypeId(),
          itemId: this.getItemId(),
          instanceId: this.instanceId,
          data: event.detail,
        });
      });
    }
  }

  initializeAnnotationEvents() {
    this.annotationPlugin.registerListener('highlightAnnotation', (event: any) => {
      if (event.detail.instanceId != this.instanceId || event.detail.itemId != this.getItemId()) {
        return;
      }
      EmitterService.get(AnnotationHighlightSelectedAnnotation).emit({
        comment: event.detail.annotation.comments[0],
        annotationId: event.detail.annotation.id,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
        open: true,
      });
    });
    this.annotationPlugin.registerListener('addingAnnotationDataChanged', (event: any) => {
      if (event.detail.instanceId != this.instanceId || event.detail.itemId != this.getItemId()) {
        return;
      }
      onAddingAnnotationDataChanged.emit({
        act: 'onAddingAnnotationDataChanged',
        data: event.detail,
        projectId: this.projectId,
        sectionId: this.sectionId,
        subSectionId: this.subsectionId,
        typeId: this.getTypeId(),
        itemId: this.getItemId(),
        instanceId: this.instanceId,
      });
    });

    // Change time format event from the plugin
    this.annotationPlugin.registerListener('timeFormatChanged', (event: any) => {
      onTimeFormatChangePlugin.emit({
        act: 'timeFormatChanged',
        format: event.detail.format,
      });
    });

    this.annotationPlugin.registerListener('annotationHoverOpened', (event: any) => {
      onAnnotationHoverOpened.emit({
        annotation: event.detail.annotation,
        itemId: this.getItemId(),
        instanceId: this.instanceId,
      });
    });

    this.annotationPlugin.registerListener('annotationHoverClosed', (event: any) => {
      onAnnotationHoverClosed.emit({
        annotation: event.detail.annotation,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
    });

    // Video specific listeners
    this.annotationPlugin.registerListener('annotationClickedOpened', (event: any) => {
      onAnnotationClickOpened.emit({
        annotation: event.detail.annotation,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
      onAnnotationClickOpenedV2.emit({
        annotation: event.detail.annotation,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
    });

    this.annotationPlugin.registerListener('annotationClickedClosed', (event: any) => {
      onAnnotationClickClosed.emit({
        annotation: event.detail.annotation,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
      onAnnotationClickClosedV2.emit({
        annotation: event.detail.annotation,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
    });

    // Image and Audio specific listeners
    this.annotationPlugin.registerListener('annotationClickedIn', (event: any) => {
      if (event.detail.instanceId != this.instanceId || event.detail.itemId != this.getItemId()) {
        return;
      }
      onAnnotationClickedIn.emit({
        act: 'onAnnotationClickedIn',
        projectId: this.projectId,
        sectionId: this.sectionId,
        subSectionId: this.subsectionId,
        typeId: this.getTypeId(),
        itemId: this.getItemId(),
        instanceId: this.instanceId,
        data: event.detail,
      });
      onAnnotationClickOpenedV2.emit({
        annotation: event.detail.annotation,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
    });

    this.annotationPlugin.registerListener('annotationClickedOut', (event: any) => {
      if (event.detail.instanceId != this.instanceId || event.detail.itemId != this.getItemId()) {
        return;
      }
      onAnnotationClickedOut.emit({
        act: 'onAnnotationClickedOut',
        projectId: this.projectId,
        sectionId: this.sectionId,
        subSectionId: this.subsectionId,
        typeId: this.getTypeId(),
        itemId: this.getItemId(),
        instanceId: this.instanceId,
        data: event.detail,
      });
      onAnnotationClickClosedV2.emit({
        annotation: event.detail.annotation,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
    });

    // this.annotationPlugin.registerListener("onAnnotationEdited", (event: any) => {
    // 	let newAnnotation = event.detail.annotation;
    // 	let oldAnnotation = _.find(this.annotations, { id: newAnnotation.id }) as any;
    // 	if (oldAnnotation.shape !== newAnnotation.shape || oldAnnotation.range != newAnnotation.range) {
    // 		this.saveAnnotation(newAnnotation);
    // 		this.annotations = event.detail.allAnnotations;
    // 	}
    // });

    this.annotationPlugin.registerListener('annotationCreated', (event: any) => {
      console.log('annotationCreated', event.detail, this.instanceId, this.getItemId(), this.item?.name);
      if (event.detail.itemId != this.getItemId() || event.detail.instanceId != this.instanceId) return;
      console.log('saving annotation', this.item?.name);
      this.saveAnnotation(event.detail.newAnnotation, event.detail.itemId, event.detail.instanceId);
      this.annotations = event.detail.allAnnotations;
    });

    this.annotationPlugin.registerListener('annotationDeleted', (event: any) => {
      if (event.detail.itemId != this.getItemId() || event.detail.instanceId != this.instanceId) return;
      this.annotations = event.detail.allAnnotations;
    });

    this.annotationPlugin.registerListener('annotationEdited', (event: any) => {
      if (event.detail.itemId != this.getItemId() || event.detail.instanceId != this.instanceId) return;
      this.saveAnnotation(event.detail.annotation, event.detail.itemId, event.detail.instanceId);
      this.annotations = event.detail.allAnnotations;
    });

    this.annotationPlugin.registerListener('commentCreated', (event: any) => {
      if (event.detail.itemId != this.getItemId() || event.detail.instanceId != this.instanceId) return;
      this.saveAnnotation(event.detail.annotation, event.detail.itemId, event.detail.instanceId);
      this.annotations = event.detail.allAnnotations;
    });

    this.annotationPlugin.registerListener('commentDeleted', (event: any) => {
      if (event.detail.itemId != this.getItemId() || event.detail.instanceId != this.instanceId) return;
      this.annotations = event.detail.allAnnotations;
    });

    this.annotationPlugin.registerListener('commentEdited', (event: any) => {
      if (event.detail.itemId != this.getItemId() || event.detail.instanceId != this.instanceId) return;
      this.saveAnnotation(event.detail.annotation, event.detail.itemId, event.detail.instanceId);
      this.annotations = event.detail.allAnnotations;
    });

    this.annotationPlugin.registerListener('enteredAddingAnnotation', (event: any) => {
      if (event.detail.itemId != this.getItemId() || event.detail.instanceId != this.instanceId) return;
      onAddingAnnotation.emit({
        act: 'addingAnnotation',
        event: event,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
    });

    this.annotationPlugin.registerListener('exitingAddingAnnotation', (event: any) => {
      console.log('exitingAddingAnnotation', event.detail, this.instanceId, this.item?.name);
      if (event.detail.itemId != this.getItemId() || event.detail.instanceId != this.instanceId) return;
      onCancelAddingAnnotation.emit({
        act: 'cancelAddingAnnotation',
        event: event,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
        type: 'mediaplayer',
      });
    });

    // Image specific listeners

    this.annotationPlugin.registerListener('imageZoomChanged', (event: any) => {
      onImageZoomChanged.emit({
        act: 'imageZoomChanged',
        zoom: event.detail.zoom,
        viewportTransform: event.detail.viewportTransform,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
    });

    this.annotationPlugin.registerListener('imageViewportChanged', (event: any) => {
      onImageViewportChanged.emit({
        act: 'imageViewportChanged',
        viewportTransform: event.detail.viewportTransform,
        zoom: event.detail.zoom,
        instanceId: this.instanceId,
        itemId: this.getItemId(),
      });
    });
  }

  reorderControlBarButtons() {
    try {
      if (!this.player?.controlBar?.el) return;
      const barEl = this.player.controlBar.el();
      if (!barEl) return;
      const vacToggle = barEl.querySelector('.vac-toggle-annot-display');
      const downloadBtn = barEl.querySelector('.vjs-vjsdownload');
      if (vacToggle && downloadBtn && vacToggle !== downloadBtn.previousSibling) {
        barEl.insertBefore(vacToggle as any, downloadBtn as any);
      }
    } catch (_) {}
  }

  getItemId() {
    return this.item?.hasPage && !!this.activePage ? this.activePage.id : this.item.id;
  }

  getTypeId() {
    return this.item?.hasPage && !!this.activePage ? this.item.id : this.typeId;
  }

  getEntityType() {
    return this.item?.hasPage && !!this.activePage ? 'PAGE' : 'ASSET';
  }

  saveAnnotation(entity: any, entityId: string, entityInstanceId: string) {
    // TODO: Remove this once the annotation is saved in the new BE
    return;
  }

  checkUrl(url: string) {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + currentAppToken(),
    });

    return this._http
      .get(url, {
        headers,
        observe: 'response',
        responseType: 'blob', // Use blob to avoid downloading entire file
      })
      .pipe(
        map((response) => {
          return response.status == 200;
        }),
        catchError((error) => {
          console.log('URL check failed:', error);
          return of(false);
        })
      );
  }

  private checkMp4(filename) {
    return filename && this.fileUtilsService.isFileExtension(filename, 'mp4');
  }

  private prepareWaveSurfer(file: any) {
    setTimeout(() => {
      if (this.mtmAudioPlayer) {
        this.mtmAudioPlayer.nativeElement.src = this.file.signedURL;
        this.mtmAudioPlayer.nativeElement.load();
        const token = currentAppToken();
        let height;
        if (this.portfolioPreview) height = 70;
        else if (this.reorderPreview) height = 100;
        else height = 200;
        this.isAudioPluginReady = false;
        this.wavesurfer = WaveSurfer.create({
          container: document.getElementById(this.uuid),
          waveColor: '#909091',
          progressColor: '#fe3b00',
          barWidth: 1,
          cursorColor: 'transparent',
          height: height,
          xhr: {
            requestHeaders: [{ key: 'Authorization', value: 'Bearer ' + token }],
          },
        });
        this.wavesurfer.on('ready', (e: any) => {
          console.log('audio player ready');
          this.isAudioPluginReady = true;
        });
        this.wavesurfer.load(file.signedURL);
        $(this.mtmAudioPlayer.nativeElement).on('timeupdate', (e: any) => {
          this.wavesurfer.setCurrentTime(e.target.currentTime);
        });
      }
    }, 1500);
  }

  getFileTypeIcon(item) {
    return 'assets/img/icons' + item.contentType.substr(item.contentType.lastIndexOf('/')) + '.svg';
  }

  formatSizeUnits(bytes) {
    if (bytes >= 1073741824) {
      bytes = (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
      bytes = (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
      bytes = (bytes / 1024).toFixed(2) + ' KB';
    } else if (bytes > 1) {
      bytes = bytes + ' bytes';
    } else if (bytes == 1) {
      bytes = bytes + ' byte';
    } else {
      bytes = '0 byte';
    }
    return bytes;
  }

  showImageInNewTab() {
    if (this.isClickableImage) window.open(this.file.signedURL);
  }

  playAsset() {
    if (this.isError) {
      return;
    }

    if (this.mediaType === 'video' && this.player) {
      this.player.play();
    } else if (this.mediaType === 'audio' && this.enableAudioAnnotation && this.audioAnnotationComponent?.wavesurfer) {
      this.audioAnnotationComponent.wavesurfer.play();
    } else if (this.mediaType === 'audio' && this.mtmAudioPlayer) {
      this.mtmAudioPlayer.nativeElement.play();
    }
  }

  pauseAsset() {
    if (this.isError) {
      return;
    }

    if (this.mediaType === 'video' && this.player) {
      this.player.pause();
    } else if (this.mediaType === 'audio' && this.enableAudioAnnotation && this.audioAnnotationComponent?.wavesurfer) {
      this.audioAnnotationComponent.wavesurfer.pause();
    } else if (this.mediaType === 'audio' && this.mtmAudioPlayer) {
      this.mtmAudioPlayer.nativeElement.pause();
    }
  }

  private performDownload() {
    onDownloadStart.emit();
    this.fileDownloadInfo = MTMFileDownload.fromFileRef(this.file);
    this.fileDownloadInfo.id =
      this.file.projectId + '-' + this.file.sectionId + '-' + this.file.subSectionId + '-' + this.file.id;
    this.fileDownloadInfo.url = this.file.signedURL;
    this.transferService.downloadFile(this.fileDownloadInfo);
    this.sendDownloadGAEvent({ url: this.fileDownloadInfo.url });
  }

  public downloadAsset() {
    if (this.mediaType == 'video') {
      let downloadControlId: any;
      if (this.file?.projectId && this.file.sectionId && this.file.subSectionId && this.file.id) {
        downloadControlId =
          'video-js-' +
          this.file.projectId +
          '-' +
          this.file.sectionId +
          '-' +
          this.file.subSectionId +
          '-' +
          this.file.id;
      }
      this.videoOptions.plugins.vjsdownload.downloadCallback(downloadControlId);
    } else if (this.mediaType == 'audio' || this.mediaType == 'image') {
      EmitterService.get('FILE:DOWNLOAD').emit(this.file);
    } else {
      if (this.file.signedURL) {
        this.performDownload();
      } else {
        this.signedUrlService.getObjectStorageInfo(this.file.id).subscribe({
          next: (response: any) => {
            if (response) {
              this.file.signedURL = response.signedURL;
              this.performDownload();
            }
          },
        });
      }
    }
  }

  mutePlayer(mute) {
    if (this.isError) {
      return;
    }

    if (this.mediaType === 'video' && this.player) {
      this.player.muted(mute);
      return;
    }

    if (this.mediaType === 'audio' && this.enableAudioAnnotation && this.audioAnnotationComponent?.wavesurfer) {
      const previousVolume = this.audioAnnotationComponent.prevVolume ?? this.audioAnnotationComponent.volume ?? 1;
      if (mute) {
        this.audioAnnotationComponent.prevVolume = previousVolume;
        this.audioAnnotationComponent.updateVolume(0);
      } else {
        this.audioAnnotationComponent.updateVolume(previousVolume || 0.2);
      }
      return;
    }

    if (this.mediaType === 'audio' && this.mtmAudioPlayer) {
      this.mtmAudioPlayer.nativeElement.muted = mute;
    }
  }

  resetAssetPlay() {
    if (this.isError) {
      return;
    }

    if (this.mediaType === 'video' && this.player) {
      this.player.currentTime(0);
    } else if (this.mediaType === 'audio' && this.mtmAudioPlayer) {
      this.mtmAudioPlayer.nativeElement.currentTime = 0;
    }
  }

  setToStart() {
    if (this.isError) {
      return;
    }

    if (this.mediaType === 'video' && this.player) {
      this.player.currentTime(0);
      return;
    }

    if (this.mediaType === 'audio' && this.enableAudioAnnotation && this.audioAnnotationComponent?.wavesurfer) {
      this.audioAnnotationComponent.wavesurfer.seekTo(0);
      return;
    }

    if (this.mediaType === 'audio' && this.mtmAudioPlayer) {
      this.mtmAudioPlayer.nativeElement.currentTime = 0;
    }
  }

  setToEnd() {
    if (this.isError) {
      return;
    }

    if (this.mediaType === 'video' && this.player) {
      this.player.currentTime(this.player.duration());
      return;
    }

    if (this.mediaType === 'audio' && this.enableAudioAnnotation && this.audioAnnotationComponent?.wavesurfer) {
      const { wavesurfer } = this.audioAnnotationComponent;
      const duration = wavesurfer.getDuration?.() ?? 0;
      if (typeof wavesurfer.seekTo === 'function') {
        wavesurfer.seekTo(1);
      } else {
        wavesurfer.seekTo(duration);
      }
      return;
    }

    if (this.mediaType === 'audio' && this.mtmAudioPlayer) {
      const audioEl = this.mtmAudioPlayer.nativeElement;
      audioEl.currentTime = audioEl.duration ?? audioEl.currentTime ?? 0;
    }
  }

  get isMediaPlayerLoaded(): boolean {
    switch (this.mediaType) {
      case 'video':
        //this.videoGap = this.hideAnnotationToolbar || !this.isVideoPlayerLoaded ? 10 : 76;
        return this.isVideoPluginReady;
      case 'audio':
        this.audioGap = this.hideAnnotationToolbar || !this.isAudioPluginReady ? 52 : 124;
        return this.isAudioPluginReady;
      //TODO: add other media types loading check
      default:
        return true;
    }
  }

  handleScanStatusChange() {
    if (this.item.malwareScanningStatus === MalwareScanningStatus.Clean) {
      this.updatePreviewSize();
    }
  }

  public toggleAssetList() {
    this.isAssetListVisible = !this.isAssetListVisible;
    this.isAssetInfoVisible = false;
  }

  public toggleAssetInfo() {
    this.isAssetInfoVisible = !this.isAssetInfoVisible;
    this.isAssetListVisible = false;
  }

  handleAssetClick() {
    this.isAssetListVisible = false;
  }

  handleAssetInfoClick() {
    this.isAssetInfoVisible = false;
  }

  //#region file replacement functionality

  checkTranscodingReplacementFile(file: MTMCustomFile) {
    if (file.uploadStatusCode === TransferStatus.QUARANTINED) {
      this.fileTranscodingStatus = 'failed';
      return;
    }

    if (file.dbFileObject?.contentType == 'image/png') {
      this.fileTranscodingStatus = 'success';
      const metadataToUpdate = this.file.transcodeMetadata?.find((c) => c.codec == 'png');
      if (metadataToUpdate) {
        metadataToUpdate.resolution = 'true';
      }
      this.file.previewSRC = this.file.signedURL + '?thumbnail=_IMG';
      return;
    }

    const codec: string = 'images';
    this.checkReplacementFile(file.extraInfo.failedTranscodingFileId, file.dbFileObject.id, codec);
  }

  checkReplacementFile(fileId: string, replacementFileId: string, codec: string) {
    this.switchFileToMonitor(fileId);
    const obs$ = this.transcodeMonitorService.monitorReplacementFile({
      fileId,
      projectId: this.projectId ?? this.projectService.project?.id,
      replacementFileId,
      codec,
      type: codec == 'images' ? TranscodeMonitorType.PagesReplacement : TranscodeMonitorType.SingleFileReplacement,
    });

    this.transcodeMonitorService[fileId] = obs$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (data: TranscodeMonitorResult | boolean) => {
        if (typeof data == 'boolean') {
          this.fileTranscodingStatus = data ? 'success' : 'failed';
          return;
        }

        const isSameFile = data.fileId === this.file.id;

        if (isSameFile) {
          this.isLoading = false;
          this.fileTranscodingStatus = data.isSuccess ? 'success' : 'failed';

          if (data.file) {
            this.fileUtilsService.copyFilePropsAfterScanning(data.file, this.file);
          }
        }

        if (data.isSuccess) {
          EmitterService.get(FileTranscodedEvent).emit(data);
        }

        if (isSameFile && data.isSuccess) {
          if (codec == 'images' || codec == 'pdf') {
            this.file.previewSRC = this.file.signedURL + '?thumbnail=_PDF';
            this.mediaType = 'pdf';
            this.initializeComments('pdf');
          }
        }
      },
    });
  }

  toggleFullscreen() {
    setTimeout(() => {
      if (
        this.annotationPlugin &&
        (this.fileTypeService.isImageFile(this.item) || this.activePage) &&
        this.imagePristine
      ) {
        this.annotationPlugin.fire('resetImageOrientation');
      }
    }, 1000);
  }

  onVideoPlayerFullscreenChange() {
    if (this.currentJsonStyleSubtitle && this.currentFontSubtitle) {
      this.attachSubtitleStyle(this.currentJsonStyleSubtitle, this.currentFontSubtitle);
    }
  }
  //#endregion
}
